import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type {
  Charger,
  ChargerConfig,
  ChargerModel,
  ConfigTemplate,
  Location,
} from "../types";
import { useToast } from "../toast";

interface Props {
  charger: Charger | null;
  existingConfig: ChargerConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function OnboardChargerDialog({
  charger,
  existingConfig,
  onClose,
  onSaved,
}: Props) {
  const api = useAuthApi();
  const [locations, setLocations] = useState<Location[]>([]);
  const [chargerModels, setChargerModels] = useState<ChargerModel[]>([]);
  const [configTemplates, setConfigTemplates] = useState<ConfigTemplate[]>([]);
  const [locationId, setLocationId] = useState<number | "">("");
  const [chargerModelId, setChargerModelId] = useState<number | "">("");
  const [configTemplateId, setConfigTemplateId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!charger) return;
    Promise.all([
      api<Location[]>(`${MGMT}/locations`),
      api<ChargerModel[]>(`${MGMT}/charger-models`),
      api<ConfigTemplate[]>(`${MGMT}/config-templates`),
    ])
      .then(([locs, models, templates]) => {
        setLocations(locs ?? []);
        setChargerModels(models ?? []);
        setConfigTemplates(templates ?? []);
      })
      .catch((e) => toast.show((e as Error).message, "error"));
    setLocationId(existingConfig?.location_id ?? "");
    setChargerModelId(existingConfig?.charger_model_id ?? "");
    setConfigTemplateId(existingConfig?.config_template_id ?? "");
  }, [charger, existingConfig, toast]);

  if (!charger) return null;

  const save = async () => {
    if (locationId === "") return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        location_id: locationId,
        charger_model_id: chargerModelId !== "" ? chargerModelId : null,
        config_template_id: configTemplateId !== "" ? configTemplateId : null,
      };
      if (existingConfig) {
        await api(`${MGMT}/charger-configs/${existingConfig.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await api(`${MGMT}/charger-configs`, {
          method: "POST",
          body: JSON.stringify({
            charge_point_id: charger.charge_point_id,
            ...payload,
          }),
        });
      }
      toast.show("Charger linked to location");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Link Charger to Location</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace" }}>
          {charger.charge_point_id}
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Location *</InputLabel>
          <Select
            label="Location *"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value as number)}
          >
            {locations.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
                {l.city ? ` — ${l.city}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Hardware Model (optional)</InputLabel>
          <Select
            label="Hardware Model (optional)"
            value={chargerModelId}
            onChange={(e) => setChargerModelId(e.target.value as number | "")}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {chargerModels.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.vendor} {m.model_name}
                {m.connectors && m.connectors.length > 0
                  ? ` — ${m.connectors.map((c) => c.connector_type).join(", ")}`
                  : ""}
                {m.max_power_kw != null ? ` ${m.max_power_kw} kW` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Config Template (optional)</InputLabel>
          <Select
            label="Config Template (optional)"
            value={configTemplateId}
            onChange={(e) => setConfigTemplateId(e.target.value as number | "")}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {configTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || locationId === ""}
          onClick={save}
        >
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
}
